import {
  closestCenter,
  DndContext,
  DragEndEvent,
  DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  useDroppable,
  useSensor,
  useSensors,
} from "@dnd-kit/core"
import { restrictToWindowEdges } from "@dnd-kit/modifiers"
import { FunctionComponent, useEffect, useState } from "react"
import {
  RootState,
  useTypedDispatch,
  useTypedSelector,
} from "../../../app/store"
import { updateChoice } from "../../simulation/simulationSlice"
import { Choice, ChoiceType } from "../types"

import DragOverlayArea from "./drag-overlay-area"

import { SortableContext, sortableKeyboardCoordinates } from "@dnd-kit/sortable"
import { useParams } from "react-router-dom"
import { MessageColors, MessageTypes } from "../../simulation-messages/types"
import { enactPlan } from "../../simulation/simulationThunks"
import {
  setActiveChoice,
  setActiveChoiceConnections,
  setActiveChoiceType,
  setAddConnectionOpenFlag,
  setSelectedTreeItem,
} from "../decisionsSlice"
import AddConnection from "./add-connection"
import DecisionPlanButtons from "./decision-plan-buttons"
import SortableItem from "./sortable-item"

type PlanItemProps = {
  choice: Choice
  isSelected?: boolean
}

const PlanItem: FunctionComponent<PlanItemProps> = ({ choice, isSelected = false }) => {
  return (
    <div className={`self-stretch transition-all duration-200 ${isSelected ? 'ring-2 ring-blue-500 ring-offset-2 rounded-lg bg-blue-50 p-2' : ''}`} key={choice.item.id}>
      <div className="flex-1 flex flex-col items-start justify-start gap-[6px]">
        <div className="self-stretch flex flex-row items-center justify-start gap-[8px]">
          <div className="flex-1 relative leading-[24px] font-medium">
            {isSelected ? (
              <span className="text-blue-600 font-semibold">Ready to Execute</span>
            ) : (
              'Plan Created'
            )}
          </div>
          <div className="relative text-xs leading-[18px] text-gray-600">
            {
              // normally time of event goes here
            }
          </div>
        </div>
        <div
          className={`${
            MessageColors[MessageTypes.PLAN]
          } ${isSelected ? 'border-2 border-blue-500' : ''} self-stretch rounded-tl-none rounded-tr-lg rounded-b-lg overflow-hidden flex flex-row items-center justify-start py-2.5 px-3.5 text-gray-900`}>
          <div className="flex-1 relative leading-[24px] font-medium">
            {choice.item.planSentence}
          </div>
        </div>
      </div>
    </div>
  )
}

type DroppableAreaProps = {
  activeId: string | null
  isDropped: boolean
}

const DroppableArea = ({ activeId, isDropped }: DroppableAreaProps) => {
  const { setNodeRef, isOver } = useDroppable({ id: "plan-droppable" })

  let content

  if (isDropped) {
    content = (
      <div>
        Drag and drop a <b>Plan</b> to execute it.
      </div>
    )
  } else if (isOver) {
    content = (
      <div>
        Drag and drop a <b>Plan</b> to execute it.
      </div>
    )
  } else {
    content = (
      <div>
        Drag and drop a <b>Plan</b> to execute it.
      </div>
    )
  }

  return (
    <div
      ref={setNodeRef}
      className={`${
        activeId ? "border-2 border-dashed border-green-500 rounded-md" : ""
      }`}>
      <div
        id="planEnactBox"
        className="text-sm text-primary-700 rounded-lg bg-base-white shadow-[0px_1px_2px_rgba(16,_24,_40,_0.05)] box-border w-full h-[98px] overflow-hidden flex flex-col py-3 px-3.5 z-[2] border-[1px] border-solid border-gray-300">
        {content}
      </div>
    </div>
  )
}

const arrayMoveImmutable = <T,>(array: T[], from: number, to: number): T[] => {
  const newArray = [...array]
  newArray.splice(
    to < 0 ? newArray.length + to : to,
    0,
    newArray.splice(from, 1)[0],
  )
  return newArray
}

const PlanPanel: FunctionComponent = () => {
  const plans = useTypedSelector((state) => state.simulationSlice.plans)
  const dispatch = useTypedDispatch()
  const [activeId, setActiveId] = useState<string>("")
  const [isDropped, setIsDropped] = useState(false)
  const {
    addConnectionOpenFlag: addConnectionFlag,
    activeChoice,
    activeChoiceConnections,
  } = useTypedSelector((state: RootState) => {
    return state.decisionsSlice
  })
  const runIdStr = useParams().runId
  const runId = runIdStr ? parseInt(runIdStr) : 0

  const [showButtons, setShowButtons] = useState(false)

  const handleDragStart = (event: DragStartEvent) => {
    if (addConnectionFlag) return
    if (event.active.id != null) {
      setActiveId(event.active.id.toString())
    }
  }

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (over && over.id === "plan-droppable") {
      setIsDropped(true)
    }
    if (over && active.id != null && over.id != null && active.id !== over.id) {
      const oldIndex = plans.findIndex(
        (plan) => plan.item.id.toString() === active.id.toString(),
      )
      const newIndex = plans.findIndex(
        (plan) => plan.item.id.toString() === over.id.toString(),
      )

      const newPlans = arrayMoveImmutable(plans, oldIndex, newIndex)
      dispatch(updateChoice(newPlans))
    }
  }

  const filterDraggingPlanItem = (activeId: string, plans: Choice[]) => {
    const overlayElement = plans.find(
      (choice) => choice.item.id.toString() === activeId,
    )

    if (overlayElement) {
      return <PlanItem choice={overlayElement} />
    }

    return <PlanItem choice={plans[0]} />
  }

  useEffect(() => {
    const choice = plans.find((plan) => plan?.item?.id?.toString() === activeId)
    if (choice && isDropped) {
      dispatch(setActiveChoice(choice))
      dispatch(setActiveChoiceType(ChoiceType.decision))
      dispatch(setActiveChoiceConnections(choice.connections ?? []))
      dispatch(setSelectedTreeItem(choice.item))
      setShowButtons(true)
    }

    return () => {
      setIsDropped(false)
      setActiveId("")
    }
  }, [isDropped])

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  )

  const handleDecisionsClick = () => {
    if (activeChoice == null) return
    const activeChoiceWithConnections = {
      ...activeChoice,
      connections: activeChoiceConnections,
      choiceType: ChoiceType.eplan,
    }
    dispatch(enactPlan(activeChoiceWithConnections, runId))
    dispatch(setActiveChoiceConnections([]))
    dispatch(setAddConnectionOpenFlag(false))
    setShowButtons(false)
  }

  return (
    <div
      id="planListContainer"
      className="w-full relative flex-1 flex flex-col items-center justify-between py-0 pr-0 box-border text-left text-lg text-gray-900 font-text-sm-medium min-h-0">
      {!addConnectionFlag && (
        <div className="self-stretch flex flex-col items-start justify-start pl-[13px] flex-shrink-0">
          <div className="self-stretch flex flex-row items-start justify-start gap-[16px]">
            <div className="self-stretch flex-1 flex flex-col items-start justify-center">
              <h4>Plans</h4>
            </div>
          </div>
        </div>
      )}

      {!addConnectionFlag && (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          modifiers={[restrictToWindowEdges]}>
          <div className="self-stretch pl-[13px] flex-1 flex flex-col overflow-y-auto items-start justify-start py-6 gap-[16px] z-[1] text-sm text-gray-700 min-h-0">
            {plans.length === 0 && "No plans yet"}
            {plans.length > 0 && (
              <SortableContext
                items={plans.filter(plan => plan.item.id != null).map((plan) => plan.item.id.toString())}>
                {plans.filter(plan => plan.item.id != null).map((plan) => (
                  <SortableItem key={plan.item.id} id={plan.item.id.toString()}>
                    <PlanItem
                      choice={plan}
                      isSelected={showButtons && plan.item.id.toString() === activeId}
                    />
                  </SortableItem>
                ))}
              </SortableContext>
            )}
            {activeId && (
              <DragOverlayArea>
                {filterDraggingPlanItem(activeId, plans)}
              </DragOverlayArea>
            )}
          </div>
          {!addConnectionFlag && !showButtons && (
            <div className="self-stretch flex-shrink-0">
              <DroppableArea activeId={activeId} isDropped={isDropped} />
            </div>
          )}
        </DndContext>
      )}

      {!addConnectionFlag && showButtons && (
        <div className="self-stretch flex-shrink-0 px-6 py-4 bg-white border-t border-gray-200">
          <DecisionPlanButtons
            handleDecisionsClick={handleDecisionsClick}
            disabledCheck={() => false}
          />
        </div>
      )}
      {addConnectionFlag && activeChoice && (
        <div
          id="addConnectionContainer"
          className="self-stretch flex flex-col flex-1 overflow-hidden items-start justify-start min-h-0">
          <AddConnection
            choice={activeChoice}
            additionalCancelAction={() => {
              setShowButtons(false)
            }}
            submitAction={handleDecisionsClick}
          />
        </div>
      )}
    </div>
  )
}

export default PlanPanel
